import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';

/**
 * Página 404 con noindex.
 * - Renderiza meta robots="noindex" para que Google no indexe rutas inexistentes.
 * - Devuelve un mensaje claro y links de navegación principales.
 * - React 19 hoists meta/link/title automáticamente al <head>.
 */
export default function NotFoundPage() {
  const location = useLocation();

  useEffect(() => {
    // Skip tracking durante el pre-render de react-snap (sino el build cuelga)
    if (typeof navigator !== 'undefined' && navigator.userAgent && /ReactSnap/i.test(navigator.userAgent)) {
      return;
    }
    // Reportar a backend para que GSC/analytics tenga visibilidad de 404s reales
    try {
      const url = process.env.REACT_APP_BACKEND_URL;
      if (url) {
        fetch(`${url}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_type: 'page_404',
            page_path: location.pathname,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-950">
      <title>Página no encontrada (404) | SinAdicciones.org</title>
      <meta name="description" content="La página que buscas no existe o ha sido movida. Vuelve al directorio principal de SinAdicciones.org." />
      <meta name="robots" content="noindex, follow" />
      <link rel="canonical" href={`https://sinadicciones.org${location.pathname}`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Página no encontrada (404) | SinAdicciones.org" />
      <meta property="og:description" content="La página que buscas no existe o ha sido movida." />
      <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
      <meta property="og:locale" content="es_CL" />
      <meta name="twitter:card" content="summary_large_image" />

      <Header />

      <main className="px-6 py-24" data-testid="not-found-page">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-cyan-400 font-mono text-sm tracking-widest mb-4">ERROR 404</p>
          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight">
            Esta página no existe
          </h1>
          <p className="text-lg text-slate-300 mb-2">
            La URL <code className="px-2 py-1 bg-slate-800 rounded text-cyan-300 text-sm">{location.pathname}</code> no corresponde a ningún contenido en sinadicciones.org.
          </p>
          <p className="text-slate-400 mb-12">
            Es posible que el enlace esté roto o que el contenido haya sido movido.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-16">
            <Link to="/" data-testid="back-home-link">
              <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                Volver al inicio
              </Button>
            </Link>
            <Link to="/centros-de-rehabilitacion-drogas-chile" data-testid="see-centers-link">
              <Button size="lg" variant="outline" className="border-slate-700 text-slate-200">
                Ver centros de rehabilitación
              </Button>
            </Link>
          </div>

          <div className="text-left bg-slate-900/60 border border-slate-800 rounded-2xl p-8">
            <h2 className="text-base font-semibold text-white mb-4 uppercase tracking-wider text-sm">
              ¿Qué buscabas?
            </h2>
            <ul className="space-y-3 text-slate-300">
              <li>
                <Link to="/calculadora" className="hover:text-cyan-400 underline-offset-4 hover:underline">
                  → Calculadora de costos de rehabilitación
                </Link>
              </li>
              <li>
                <Link to="/reto-21-dias" className="hover:text-cyan-400 underline-offset-4 hover:underline">
                  → Reto 21 días sin drogas (gratis)
                </Link>
              </li>
              <li>
                <Link to="/profesionales" className="hover:text-cyan-400 underline-offset-4 hover:underline">
                  → Profesionales en adicciones
                </Link>
              </li>
              <li>
                <Link to="/grupos-aa" className="hover:text-cyan-400 underline-offset-4 hover:underline">
                  → Grupos de Alcohólicos y Narcóticos Anónimos
                </Link>
              </li>
              <li>
                <Link to="/contacto" className="hover:text-cyan-400 underline-offset-4 hover:underline">
                  → Contactarnos
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
